import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import { useServiceRequestDashboardApi, UseServiceRequestDashboardApiReturn } from 'src/services/api/ServiceRequestDashboard/serviceRequestDashboard';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { CsvDateFormat } from 'src/services/client-types';
import {
  useDownloadCSVData,
  UseDownloadCSVDataReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadCSV/useDownloadCSVData';
import {
  useDownloadPDF,
  UseDownloadPDFReturn,
} from 'src/pages/SharedPageCustomHooks/UseDownloadPDF/useDownloadPDF';
import {
  useServiceRequestAPI,
  UseServiceRequestAPIReturn,
} from 'src/pages/ServiceRequest/CustomHooks/useServiceRequestAPI';
import {
  usePropertyServiceRequestsFormState,
  UsePropertyServiceRequestsFormStateReturn,
} from './usePropertyServiceRequestsFormState';
import { SRStatus, SR_STATUS } from './constants';
import {
  BulkDeclinePayload,
  BulkStatusPayload,
  GroupPayload,
  toIdRefs,
} from './payloadBuilders';
import { useBulkStatusUpdate } from 'src/pages/ServiceRequests/CustomHooks/useBulkStatusUpdate';

export interface UsePropertyServiceRequestsOrchestratorArgs {
  propertyId: string;
}

export interface UsePropertyServiceRequestsOrchestratorReturn {
  serviceRequests: IServiceRequest[];
  visibleServiceRequests: IServiceRequest[];
  isLoading: boolean;
  refetch: () => Promise<void>;

  searchQuery: string;
  setSearchQuery: (query: string) => void;

  showResolvedOnly: boolean;
  toggleShowResolved: () => void;

  selectedIds: string[];
  selectedServiceRequests: IServiceRequest[];
  allVisibleSelected: boolean;
  idsForCsvExport: string[];
  toggleSelection: (id: string) => void;
  toggleSelectionBatch: (ids: string[]) => void;
  toggleSelectAllVisible: () => void;
  clearSelection: () => void;

  expandedGroupIds: string[];
  toggleGroupExpansion: (id: string) => void;

  bulkUpdateStatus: (status: string) => Promise<void>;
  bulkDecline: (reason: string, reasonType: string) => Promise<void>;

  createGroup: (groupName: string, ids: string[]) => Promise<void>;
  addToGroup: (groupId: string, ids: string[]) => Promise<void>;
  removeFromGroup: (serviceRequestId: string) => Promise<void>;
  deleteGroup: (groupId: string) => Promise<void>;

  isDeclineModalOpen: boolean;
  openDeclineModal: () => void;
  closeDeclineModal: () => void;

  isAssignOverlayOpen: boolean;
  openAssignOverlay: () => void;
  closeAssignOverlay: () => void;

  isGroupingModalOpen: boolean;
  openGroupingModal: () => void;
  closeGroupingModal: () => void;

  isDownloadOverlayOpen: boolean;
  openDownloadOverlay: () => void;
  closeDownloadOverlay: () => void;

  onDownloadCSV: (dateFormat: CsvDateFormat) => Promise<boolean>;
  onDownloadPDF: () => Promise<boolean>;
  isCSVLoading: boolean;
  isPDFLoading: boolean;

  showColumnSelectionDialog: boolean;
  openColumnSelectionDialog: () => void;
  closeColumnSelectionDialog: () => void;

  reset: () => void;
}

export const usePropertyServiceRequestsOrchestrator = ({
  propertyId,
}: UsePropertyServiceRequestsOrchestratorArgs): UsePropertyServiceRequestsOrchestratorReturn => {
  const user: any = useSelector(selectAuthenticatedUser);
  const userId: string = user?.id ?? '';

  const serviceRequestApi: UseServiceRequestAPIReturn = useServiceRequestAPI();

  const srDashboardApi: UseServiceRequestDashboardApiReturn =
    useServiceRequestDashboardApi();

  const serviceRequests: IServiceRequest[] = srDashboardApi.serviceRequests;
  const isLoading: boolean = srDashboardApi.listLoading;

  const formState: UsePropertyServiceRequestsFormStateReturn =
    usePropertyServiceRequestsFormState({ serviceRequests });

  const csvDownload: UseDownloadCSVDataReturn = useDownloadCSVData();
  const pdfDownload: UseDownloadPDFReturn = useDownloadPDF();

  const onDownloadCSV = async (dateFormat: CsvDateFormat): Promise<boolean> => {
    return await csvDownload.onDownloadCSV(formState.idsForCsvExport, dateFormat);
  };

  const onDownloadPDF = async (): Promise<boolean> => {
    return await pdfDownload.onDownloadPDF(
      userId,
      formState.idsForCsvExport.join('&')
    );
  };

  const refetch = async (): Promise<void> => {
    formState.clearSelection();
    await srDashboardApi.fetchByProperty(propertyId);
  };

  useEffect(() => {
    if (propertyId) {
      void refetch();
    }
  }, [propertyId]);

  const { bulkUpdateStatus } = useBulkStatusUpdate({
    selectedIds: formState.selectedIds,
    buildStatusPayload: (status: string): BulkStatusPayload => ({
      serviceRequest: toIdRefs(formState.selectedIds),
      name: status as SRStatus,
      userId,
      propertyId,
    }),
    onAssignRequested: formState.openAssignOverlay,
    onDeclineRequested: formState.openDeclineModal,
    updateStatus: serviceRequestApi.bulkUpdateStatus,
    refetch,
  });

  const bulkDecline = async (
    reason: string,
    reasonType: string
  ): Promise<void> => {
    if (formState.selectedIds.length === 0) {
      return;
    }

    const payload: BulkDeclinePayload = {
      serviceRequest: toIdRefs(formState.selectedIds),
      name: SR_STATUS.Declined,
      userId,
      Reason: reason,
      ReasonType: reasonType,
    };

    const ok: boolean = await serviceRequestApi.bulkUpdateStatus(payload);

    if (ok) {
      formState.closeDeclineModal();
      await refetch();
    }
  };

  const createGroup = async (
    groupName: string,
    ids: string[]
  ): Promise<void> => {
    if (ids.length === 0) {
      return;
    }

    const payload: GroupPayload = {
      GroupName: groupName,
      id: null,
      PropertyId: propertyId,
      UserId: userId,
      Subject: null,
      Description: null,
      SuiteId: null,
      AssetId: null,
      IssueTypeId: null,
      IssueTypeName: null,
      ServiceRequest: toIdRefs(ids),
    };

    const ok: boolean = await serviceRequestApi.groupServiceRequests(payload);

    if (ok) {
      formState.closeGroupingModal();
      await refetch();
    }
  };

  const addToGroup = async (
    groupId: string,
    ids: string[]
  ): Promise<void> => {
    if (ids.length === 0) {
      return;
    }

    const payload: GroupPayload = {
      GroupName: null,
      id: groupId,
      PropertyId: propertyId,
      UserId: userId,
      Subject: null,
      Description: null,
      SuiteId: null,
      AssetId: null,
      IssueTypeId: null,
      IssueTypeName: null,
      ServiceRequest: toIdRefs(ids),
    };

    const ok: boolean = await serviceRequestApi.groupServiceRequests(payload);

    if (ok) {
      formState.closeGroupingModal();
      await refetch();
    }
  };

  const removeFromGroup = async (
    serviceRequestId: string
  ): Promise<void> => {
    const confirmed: boolean = window.confirm(
      'Are you sure you want to remove this service request from the group?'
    );

    if (!confirmed) {
      return;
    }

    const ok: boolean = await serviceRequestApi.removeServiceRequestFromGroup(
      serviceRequestId,
      userId
    );

    if (ok) {
      await refetch();
    }
  };

  const deleteGroup = async (groupId: string): Promise<void> => {
    const confirmed: boolean = window.confirm(
      'Are you sure you want to delete this group?'
    );

    if (!confirmed) {
      return;
    }

    const ok: boolean = await serviceRequestApi.deleteServiceRequestGroupById(
      groupId,
      userId
    );

    if (ok) {
      await refetch();
    }
  };

  const openGroupingModal = (): void => {
    if (formState.selectedIds.length === 0) {
      toast.error('Please select at least one service request to group.', {
        autoClose: 6000,
        position: 'top-center',
      });
      return;
    }

    formState.openGroupingModal();
  };

  const reset = (): void => {
    formState.reset();
  };

  return {
    serviceRequests,
    visibleServiceRequests: formState.visibleServiceRequests,
    isLoading,
    refetch,

    searchQuery: formState.searchQuery,
    setSearchQuery: formState.setSearchQuery,

    showResolvedOnly: formState.showResolvedOnly,
    toggleShowResolved: formState.toggleShowResolved,

    selectedIds: formState.selectedIds,
    selectedServiceRequests: formState.selectedServiceRequests,
    allVisibleSelected: formState.allVisibleSelected,
    idsForCsvExport: formState.idsForCsvExport,
    toggleSelection: formState.toggleSelection,
    toggleSelectionBatch: formState.toggleSelectionBatch,
    toggleSelectAllVisible: formState.toggleSelectAllVisible,
    clearSelection: formState.clearSelection,

    expandedGroupIds: formState.expandedGroupIds,
    toggleGroupExpansion: formState.toggleGroupExpansion,

    bulkUpdateStatus,
    bulkDecline,

    createGroup,
    addToGroup,
    removeFromGroup,
    deleteGroup,

    isDeclineModalOpen: formState.isDeclineModalOpen,
    openDeclineModal: formState.openDeclineModal,
    closeDeclineModal: formState.closeDeclineModal,

    isAssignOverlayOpen: formState.isAssignOverlayOpen,
    openAssignOverlay: formState.openAssignOverlay,
    closeAssignOverlay: formState.closeAssignOverlay,

    isGroupingModalOpen: formState.isGroupingModalOpen,
    openGroupingModal,
    closeGroupingModal: formState.closeGroupingModal,

    isDownloadOverlayOpen: formState.isDownloadOverlayOpen,
    openDownloadOverlay: formState.openDownloadOverlay,
    closeDownloadOverlay: formState.closeDownloadOverlay,

    onDownloadCSV,
    onDownloadPDF,
    isCSVLoading: csvDownload.isLoading,
    isPDFLoading: pdfDownload.isLoading,

    showColumnSelectionDialog: formState.showColumnSelectionDialog,
    openColumnSelectionDialog: formState.openColumnSelectionDialog,
    closeColumnSelectionDialog: formState.closeColumnSelectionDialog,

    reset,
  };
};
